/**
 * Score → colour tier mapping shared by every oral-exam score visual on BOTH
 * web and mobile: the big report gauge (`ScoreArc`), the small list-row ring
 * (`ScoreDonut` / `OralExamCard` chip), and the dashboard score chip. One
 * source of truth so a 64% reads as the exact same hue everywhere, on every
 * platform.
 *
 *   98-100 → primary purple (exceptional — brand colour, reserved for the
 *            very top so it stays meaningful)
 *   70-97  → green (solid, doing well)
 *   50-69  → amber (shaky, terminology drift)
 *   0-49   → red (needs revisiting)
 *
 * Each tier carries BOTH raw-hex roles (used by mobile's react-native-svg
 * gauge + inline styles, where there's no Tailwind) AND web Tailwind class
 * names. The hex roles are the matching Tailwind shades of each tier's hue,
 * so the two renderings are pixel-identical:
 *   soft → -100 (track / borders / chip bg) · light → -300 (gradient start)
 *   accent → -500 (solid progress) · dark/strong → -700 · deep → -950
 *   fade → -50 (near-white panel wash)
 */

export interface Tier {
  /** Lighter gradient stop (left side of the arc). Raw hex (~-300 shade). */
  light: string;
  /** Darker gradient stop (right side of the arc). Raw hex (-700 shade). */
  dark: string;
  /** Solid mid-tone accent (raw hex, -500) for small rings like `ScoreDonut`
   *  and mobile's solid gauge progress, where a gradient is illegible. */
  accent: string;
  /** Soft -100 tint (raw hex) — gauge track, card borders, chip backgrounds
   *  on mobile (web uses the `*Class` equivalents). */
  soft: string;
  /** Strong -700 (raw hex) — the big percentage figure on mobile. */
  strong: string;
  /** Deep -950 (raw hex) — headline text on the tinted report card (mobile). */
  deep: string;
  /** Near-white -50 (raw hex) — the blurred fade panel behind the headline. */
  fade: string;
  /** Tailwind class for the empty track behind the arc/ring (`-100` shade). */
  trackClass: string;
  /** Tailwind class for the big percentage number. */
  numberClass: string;
  /** Tailwind class for the smaller "%" suffix. */
  percentClass: string;
  /** Tailwind class for the `from-` stop of the score-panel bottom-fade
   *  gradient. Consumed by `<ScorePanel>` in `oral-exam-report.tsx`. */
  gradientFromClass: string;
  /** Tailwind border class for the score panel's outer border, so the
   *  panel chrome carries the tier accent too. Consumed by
   *  `<ScorePanel>` in `oral-exam-report.tsx`. */
  panelBorderClass: string;
  /** Tailwind bg tint for score chips (dashboard cards). Pairs with
   *  `numberClass` for the chip's text. */
  chipBgClass: string;
}

const TIERS = {
  // 70-97 — "solid" range. Green reads as "you're doing well".
  green: {
    light: '#86efac',
    dark: '#15803d',
    accent: '#22c55e',
    soft: '#dcfce7',
    strong: '#15803d',
    deep: '#052e16',
    fade: '#f0fdf4',
    trackClass: 'stroke-valid-100',
    numberClass: 'text-valid-700',
    percentClass: 'text-valid-500',
    gradientFromClass: 'from-valid-200/50',
    panelBorderClass: 'border-valid-200',
    chipBgClass: 'bg-valid-100',
  },
  // 98-100 — exceptional. Brand-primary purple reserved for the very top.
  purple: {
    light: '#a78bfa',
    dark: '#6d28d9',
    accent: '#8b5cf6',
    soft: '#ede9fe',
    strong: '#6d28d9',
    deep: '#2e1065',
    fade: '#f5f3ff',
    trackClass: 'stroke-primary-100',
    numberClass: 'text-primary-700',
    percentClass: 'text-primary-400',
    gradientFromClass: 'from-primary-300/40',
    panelBorderClass: 'border-primary-200',
    chipBgClass: 'bg-primary-100',
  },
  // 50-69 — shaky / terminology drift.
  amber: {
    light: '#fcd34d',
    dark: '#b45309',
    accent: '#f59e0b',
    soft: '#fef3c7',
    strong: '#b45309',
    deep: '#451a03',
    fade: '#fffbeb',
    trackClass: 'stroke-deck-amber-100',
    numberClass: 'text-deck-amber-800',
    percentClass: 'text-deck-amber',
    gradientFromClass: 'from-deck-amber-100/70',
    panelBorderClass: 'border-amber-200',
    chipBgClass: 'bg-deck-amber-100',
  },
  // 0-49 — needs serious revisiting.
  red: {
    light: '#fca5a5',
    dark: '#b91c1c',
    accent: '#f87171',
    soft: '#fee2e2',
    strong: '#b91c1c',
    deep: '#450a0a',
    fade: '#fef2f2',
    trackClass: 'stroke-alert-100',
    numberClass: 'text-alert-700',
    percentClass: 'text-alert-500',
    gradientFromClass: 'from-alert-200/50',
    panelBorderClass: 'border-alert-200',
    chipBgClass: 'bg-alert-100',
  },
} as const satisfies Record<string, Tier>;

/** The four tier buckets by name — for consumers (e.g. the shared sound
 *  manifest) that key behavior off the tier rather than its styling. */
export type TierName = keyof typeof TIERS;

export function pickTierName(percent: number): TierName {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  // 98+ is the "exceptional" band — brand purple, reserved for the very
  // top so it stays meaningful. 70-97 is the broad "solid" band — green.
  // 50-69 amber for shaky; everything below 50 is red.
  if (clamped >= 98) return 'purple';
  if (clamped >= 70) return 'green';
  if (clamped >= 50) return 'amber';
  return 'red';
}

export function pickTier(percent: number): Tier {
  return TIERS[pickTierName(percent)];
}
